import { useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import { BarChart3, Clock, Users, Trophy, Loader2, CalendarClock } from "lucide-react";
import { serverStore } from "../../stores/server.store";
import { Card, CardHeader, CardTitle, CardContent } from "../design/Card";
import { EmptyState } from "../design/EmptyState";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";

interface PlayerStat {
  name: string;
  sessions: number;
  totalSeconds: number;
  firstSeen: number;
  lastSeen: number;
  online: boolean;
}

/** Sekunden -> "3h 12m" / "12m" / "45s". */
const fmtDuration = (sec: number) => {
  if (!sec || sec < 1) return "0m";
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m`;
  return `${Math.round(sec)}s`;
};

const fmtRelative = (ts: number) => {
  if (!ts) return "—";
  const diff = Date.now() - ts;
  const min = Math.floor(diff / 60000);
  if (min < 1) return "gerade eben";
  if (min < 60) return `vor ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `vor ${h} h`;
  const d = Math.floor(h / 24);
  if (d < 30) return `vor ${d} d`;
  return new Date(ts).toLocaleDateString("de-DE");
};

const StatTile = ({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) => (
  <div className="flex items-center gap-3 bg-white/[0.03] border border-line rounded-xl px-4 py-3">
    <div className="w-9 h-9 rounded-lg bg-ember/10 flex items-center justify-center text-ember shrink-0">{icon}</div>
    <div className="min-w-0">
      <div className="text-lg font-display font-semibold text-fg leading-tight">{value}</div>
      <div className="text-[11px] text-fg-muted uppercase tracking-wide">{label}</div>
    </div>
  </div>
);

export const PlayerStats = observer(() => {
  const server = serverStore.activeServer;
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      if (!server) return;
      const d = await serverStore.fetchPlayerStats(server.id, 30);
      if (alive) { setData(d); setLoading(false); }
    };
    setLoading(true);
    load();
    // Solange der Server laeuft, alle 15s aktualisieren (Live-Spielzeit).
    const iv = server?.status === "online" ? setInterval(load, 15000) : null;
    return () => { alive = false; if (iv) clearInterval(iv); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [server?.id, server?.status]);

  if (!server) return null;
  if (loading) {
    return <div className="flex items-center gap-2 text-fg-muted py-10"><Loader2 className="animate-spin" size={18} /> Lade Spieler-Statistik…</div>;
  }

  const players: PlayerStat[] = data?.players || [];
  const activity = data?.activity || [];
  const totals = data?.totals || { uniquePlayers: 0, totalSeconds: 0, onlineNow: 0 };
  const hasActivity = activity.some((a: any) => a.sessions > 0);

  if (players.length === 0) {
    return (
      <EmptyState
        icon={<BarChart3 size={20} />}
        title="Noch keine Spieldaten"
        description="Sobald Spieler den Server betreten und wieder verlassen, sammeln sich hier Spielzeiten und eine Aktivitätskurve. (Nur für Minecraft-Server.)"
      />
    );
  }

  const maxPlaytime = players[0]?.totalSeconds || 1;

  return (
    <div className="space-y-6">
      {/* Kennzahlen */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <StatTile icon={<Users size={17} />} label="Einzigartige Spieler" value={String(totals.uniquePlayers)} />
        <StatTile icon={<Clock size={17} />} label="Gesamte Spielzeit" value={fmtDuration(totals.totalSeconds)} />
        <StatTile icon={<CalendarClock size={17} />} label="Gerade online" value={String(totals.onlineNow)} />
      </div>

      {/* Aktivitäts-Chart (letzte 30 Tage) */}
      <Card className="bg-ink border-line-strong text-white" noPadding>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><BarChart3 size={16} className="text-ember" /> Aktivität (30 Tage)</CardTitle></CardHeader>
        <CardContent>
          {hasActivity ? (
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={activity} margin={{ top: 4, right: 8, left: -18, bottom: 0 }}>
                  <defs>
                    <linearGradient id="pstatsFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="var(--color-ember, #FF6A3D)" stopOpacity={0.5} />
                      <stop offset="100%" stopColor="var(--color-ember, #FF6A3D)" stopOpacity={0.03} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" vertical={false} />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 10, fill: "rgba(255,255,255,0.4)" }}
                    tickFormatter={(d: string) => d.slice(5).replace("-", ".")}
                    interval={Math.max(0, Math.floor(activity.length / 8))}
                  />
                  <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "rgba(255,255,255,0.4)" }} width={28} />
                  <Tooltip
                    contentStyle={{ background: "#15171e", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, fontSize: 12 }}
                    labelStyle={{ color: "#fff" }}
                    labelFormatter={(d: string) => new Date(d).toLocaleDateString("de-DE", { weekday: "short", day: "2-digit", month: "2-digit" })}
                    formatter={(v: any, n: string) => [v, n === "sessions" ? "Sitzungen" : "Spieler"]}
                  />
                  <Area type="monotone" dataKey="sessions" stroke="var(--color-ember, #FF6A3D)" strokeWidth={2} fill="url(#pstatsFill)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <p className="text-sm text-fg-muted py-8 text-center">In den letzten 30 Tagen keine Beitritte aufgezeichnet.</p>
          )}
        </CardContent>
      </Card>

      {/* Bestenliste nach Spielzeit */}
      <Card className="bg-ink border-line-strong text-white" noPadding>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><Trophy size={16} className="text-ember" /> Spielzeit-Rangliste</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {players.map((p, i) => (
            <div key={p.name} className="flex items-center gap-3 bg-white/[0.02] border border-line rounded-lg px-3 py-2">
              <span className="w-5 text-center text-xs font-mono text-fg-muted shrink-0">{i + 1}</span>
              <div className="relative shrink-0">
                <img src={`https://mc-heads.net/avatar/${p.name}/32`} alt={p.name} className="w-8 h-8 rounded-lg" onError={(e) => ((e.target as HTMLImageElement).style.visibility = "hidden")} />
                {p.online && <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-online border-2 border-ink" title="Online" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-fg truncate">{p.name}</span>
                  {p.online && <span className="text-[10px] text-online font-semibold uppercase">online</span>}
                </div>
                {/* Spielzeit-Balken relativ zum Spitzenreiter */}
                <div className="mt-1 h-1.5 rounded-full bg-white/[0.05] overflow-hidden">
                  <div className="h-full bg-ember/70 rounded-full" style={{ width: `${Math.max(3, (p.totalSeconds / maxPlaytime) * 100)}%` }} />
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="text-sm font-mono text-fg">{fmtDuration(p.totalSeconds)}</div>
                <div className="text-[10px] text-fg-muted">{p.sessions}× · {fmtRelative(p.lastSeen)}</div>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
});

export default PlayerStats;
